import moment from "moment";
import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { CustomFieldType } from "src/api/contractGroup/contractgroup-type";
import { Order, PageMeta } from "src/api/public-types";
import { PartnerMemberListModel, PartnerMemberListParam } from "src/api/userAdditionalInfo/userAdditionalInfo-type";
import { BaseButton, BaseDatePicker, BaseInput, BaseInputWithSearch, BaseSelect, BaseTable } from "src/components";
import { MultiSelectFilter } from "src/components/multiSelect";
import MetaTag from "src/components/layout/MetaTag";
import SearchResetButton from "src/components/SearchResetButton";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import { PagePath } from "src/pages/product/details";
import { qsParse, toPhoneSearchValue } from "src/utils";
import { useUserAdditionalInfo } from "../hooks/useUserAdditionalInfo";
import { PARTNER_MEMBER_SEARCH_TYPE_OPTIONS } from "../userAdditionalInfo-constant";
import { useUserAdditionalInfoColumns } from "./columns/useUserAdditionalInfoColumns";

type SearchQueryParams = PartnerMemberListParam & { searchKeyword?: string };

/**
 * 안내/설정 > 이용자 부가정보 > 목록 화면
 * - 파트너 하위 이용자를 회원번호 기준 리스트업 (탈퇴 회원 서버에서 제외)
 * - 조건 검색 / 커스텀필드 검색 + 고정 컬럼 + 동적 커스텀 컬럼
 */
const UserAdditionalInfoList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthority } = usePartnerAuthority();

  const queryParams: SearchQueryParams = useMemo(() => {
    const query: SearchQueryParams = qsParse(location.search) as SearchQueryParams;
    if (!query.page) query.page = 0;
    if (!query.size) query.size = 20;
    if (!query.sort) {
      query.sort = { orders: [{ property: "createdDate", direction: "DESC" }] };
    }
    return query;
  }, [location]);

  const [partnerMemberList, setPartnerMemberList] = useState<PartnerMemberListModel[]>([]);
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const [searchParams, setSearchParams] = useState<SearchQueryParams>({ ...queryParams });
  const [customFieldSearchParams, setCustomFieldSearchParams] = useState<{ inputValue?: string; optionIdList?: string; fieldId?: number }>({
    inputValue: queryParams.inputValue,
    optionIdList: queryParams.optionIdList,
    fieldId: queryParams.fieldId,
  });

  const { getPartnerMemberList } = useUserAdditionalInfo();
  const { columns, customFields } = useUserAdditionalInfoColumns();

  // 커스텀 필드 검색 옵션 (노출 필드만)
  const customFieldSearchOptions = useMemo(() => {
    return [
      { value: 0, label: "전체", fieldType: "" as CustomFieldType },
      ...customFields
        .filter((field) => field.isVisible)
        .map((field) => ({ value: field.fieldId, label: field.fieldName!, fieldType: field.fieldType! })),
    ];
  }, [customFields]);

  const selectedCustomFieldType = useMemo(() => {
    return customFieldSearchOptions.find((option) => String(option.value) === String(customFieldSearchParams.fieldId))?.fieldType;
  }, [customFieldSearchOptions, customFieldSearchParams.fieldId]);

  // URL 파라미터 → 상태 동기화
  useEffect(() => {
    setSearchParams({
      ...queryParams,
      searchKeyword: queryParams.searchValue,
    });
    setCustomFieldSearchParams({
      inputValue: queryParams.inputValue,
      optionIdList: queryParams.optionIdList,
      fieldId: queryParams.fieldId,
    });
  }, [queryParams]);

  // 목록 조회
  useEffect(() => {
    const fetchList = async () => {
      try {
        // 미선택(빈 문자열)은 RCR31 의 "ALL" 로 변환해 보낸다
        const { data } = await getPartnerMemberList({ ...queryParams, searchType: queryParams.searchType || "ALL" });
        setPartnerMemberList(data.data.content || []);
        setPageMeta(data.meta.pageMeta);
      } catch (error) {
        setPartnerMemberList([]);
      }
    };
    fetchList();
  }, [queryParams]);

  const navigateWithQueryParams = (passParams?: SearchQueryParams, includeSearch = false) => {
    const allowedParams: SearchQueryParams = {
      page: queryParams.page,
      size: queryParams.size,
      searchType: queryParams.searchType,
      searchValue: queryParams.searchValue,
      fieldId: queryParams.fieldId,
      inputValue: queryParams.inputValue,
      optionIdList: queryParams.optionIdList,
    };

    // 조건검색 실행 시 searchType + searchValue 반영
    const searchPart: SearchQueryParams = includeSearch
      ? {
          searchType: searchParams.searchType,
          searchValue: toPhoneSearchValue(searchParams.searchType, searchParams.searchKeyword, "PHONE_NUMBER"),
        }
      : {};

    const merged = { ...allowedParams, ...searchPart, ...(passParams || {}) };
    // searchKeyword 는 URL 에 남기지 않음
    delete (merged as SearchQueryParams).searchKeyword;

    const cleanParams = Object.fromEntries(Object.entries(merged).filter(([_, value]) => value !== undefined && value !== ""));
    // 디코딩해서 넣으면 검색어의 %, &, # 가 URL에서 깨지므로 인코딩된 채로 이동
    navigate(location.pathname + "?" + qs.stringify(cleanParams, { allowDots: true }));
  };

  return (
    <div className="page-product-list">
      <MetaTag subTitle={"이용자 부가정보"} />
      <div className="contents-container__table">
        <div className="contents-container__search-wrap">
          <div className="left-area">
            <section>
              <div className="left-area__index">
                <span>조건검색</span>
              </div>
              <div className="left-area__contents">
                <div className="flex-center">
                  <BaseInputWithSearch
                    type="text"
                    className="mr10"
                    selectValue={searchParams?.searchType}
                    inputValue={searchParams?.searchKeyword}
                    stateOptions={PARTNER_MEMBER_SEARCH_TYPE_OPTIONS}
                    setStateValue={(searchType: string) => setSearchParams({ ...searchParams, searchType })}
                    onChange={(searchKeyword: string) => setSearchParams({ ...searchParams, searchKeyword })}
                    onSearchClick={() => navigateWithQueryParams({ page: 0 }, true)}
                    onKeyUp={() => navigateWithQueryParams({ page: 0 }, true)}
                    onClearClick={() => {
                      setSearchParams({ ...searchParams, searchKeyword: undefined });
                      navigateWithQueryParams({ searchType: undefined, searchValue: undefined, page: 0 });
                    }}
                  />
                </div>

                <div className="flex-center">
                  <div className="search-divider mr10"></div>
                  <div className="left-area__index pt0">
                    <span className="minmax80 ">커스텀 필드 검색</span>
                  </div>
                  {/* 커스텀 필드 선택 */}
                  <BaseSelect
                    className="minmax240"
                    value={customFieldSearchOptions.find((item) => String(item.value) === String(customFieldSearchParams.fieldId))?.value || ""}
                    stateOptions={customFieldSearchOptions}
                    setStateValue={(value: string) => {
                      if (Number(value) === 0) {
                        setCustomFieldSearchParams({ fieldId: undefined, inputValue: undefined, optionIdList: undefined });
                        navigateWithQueryParams({ ...queryParams, fieldId: undefined, inputValue: undefined, optionIdList: undefined, page: 0 });
                        return;
                      }
                      const foundOption = customFieldSearchOptions?.find((option) => String(option.value) === String(value));
                      setCustomFieldSearchParams({ fieldId: foundOption?.value, inputValue: undefined, optionIdList: undefined });
                    }}
                    placeholder="커스텀 필드"
                  />

                  {/* 텍스트/숫자/URL 검색 */}
                  {(selectedCustomFieldType === "TEXT" || selectedCustomFieldType === "NUMBER" || selectedCustomFieldType === "URL") && (
                    <BaseInput
                      type="text"
                      className="ml10 minmax200"
                      value={customFieldSearchParams.inputValue || ""}
                      placeholder="검색어를 입력하세요"
                      onChange={(value: string) => setCustomFieldSearchParams({ ...customFieldSearchParams, inputValue: value })}
                      onSearchClick={() => navigateWithQueryParams({ ...customFieldSearchParams, optionIdList: undefined, page: 0 }, true)}
                      onKeyUp={() => navigateWithQueryParams({ ...customFieldSearchParams, optionIdList: undefined, page: 0 }, true)}
                    />
                  )}

                  {/* 캘린더 검색 */}
                  {selectedCustomFieldType === "CALENDAR" && (
                    <BaseDatePicker
                      className="ml10 minmax200"
                      type="date"
                      selectedDate={customFieldSearchParams.inputValue ? moment(customFieldSearchParams.inputValue).toDate() : undefined}
                      setDate={(date: any) => {
                        setCustomFieldSearchParams({ ...customFieldSearchParams, inputValue: date });
                        navigateWithQueryParams({ ...customFieldSearchParams, inputValue: date, optionIdList: undefined, page: 0 }, true);
                      }}
                    />
                  )}

                  {/* 단일/다중 선택 검색 */}
                  {(selectedCustomFieldType === "CHECKBOX" || selectedCustomFieldType === "RADIO") && (
                    <div className="ml10 minmax200">
                      <MultiSelectFilter
                        className="ml10 "
                        options={
                          customFields
                            .find((item) => String(item.fieldId) === String(customFieldSearchParams.fieldId))
                            ?.optionList?.map((option) => ({ value: String(option.optionId), label: option.optionName })) || []
                        }
                        value={(customFieldSearchParams.optionIdList?.split(",").map(String) as string[]) || []}
                        onChange={(values) => {
                          const optionIdListValue = values.join(",");
                          setCustomFieldSearchParams({ ...customFieldSearchParams, optionIdList: optionIdListValue });
                          navigateWithQueryParams({ ...customFieldSearchParams, optionIdList: optionIdListValue, inputValue: undefined, page: 0 }, true);
                        }}
                        placeholder="선택해주세요"
                      />
                    </div>
                  )}
                </div>
                <SearchResetButton />
              </div>
            </section>
          </div>
          <div className="right-area flex-direction-column flex-start-start">
            {isAuthority("w") && (
              <div className="flex-center">
                <BaseButton className="color-white" title="커스텀 필드 관리" onClick={() => navigate(PagePath.userAdditionalInfo.customField)} />
              </div>
            )}
          </div>
        </div>
        <BaseTable
          data={partnerMemberList}
          currentSize={Number(queryParams.size) ?? 20}
          sizeOption={(sizeValue) => navigateWithQueryParams({ ...queryParams, size: sizeValue, page: 0 })}
          pageIndex={Number(queryParams.page) || 0}
          totalElements={pageMeta?.totalElements}
          totalPages={pageMeta?.totalPages || 1}
          goPage={(page: number) => navigateWithQueryParams({ page })}
          orders={[]}
          setOrders={(orders?: Array<Order>) => {}}
          columns={columns}
          disabledSortHeaders={columns.map((column) => column.accessor as string)}
        />
      </div>
    </div>
  );
};

export default UserAdditionalInfoList;
